// Header.js - Compact Language Selector & Icon-Only Login
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaBars, 
  FaTimes, 
  FaGlobe, 
  FaBriefcase, 
  FaImages,
  FaHome,
  FaInfoCircle,
  FaBuilding,
  FaFemale,
  FaSignOutAlt,
  FaSignInAlt,
  FaChevronDown,
  FaChevronUp,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhoneAlt,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaWhatsapp,
  FaUserPlus,
  FaChartBar,
  FaSearch
} from 'react-icons/fa';
import { useTranslation, useLanguage } from '../contexts/TranslationContext';
import TraineeRegistrationModal from './TraineeRegistrationModal';
import SearchBar from './SearchBar';
import './Header.css';

const Header = ({ user, onLogout, onLoginClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showTopBar, setShowTopBar] = useState(true);
  const [topBarHeight, setTopBarHeight] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showTraineeModal, setShowTraineeModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  
  const topBarRef = useRef(null);
  const hireButtonRef = useRef(null);
  const animationRef = useRef(null);
  const searchInputRef = useRef(null);
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();

  // Language options with flags
  const languages = [
    { code: 'english', name: 'English', flag: '🇺🇸' },
    { code: 'kinyarwanda', name: 'Kinyarwanda', flag: '🇷🇼' },
    { code: 'french', name: 'Français', flag: '🇫🇷' },
    { code: 'kiswahili', name: 'Kiswahili', flag: '🇹🇿' },
    { code: 'arabic', name: 'العربية', flag: '🇸🇦' },
    { code: 'chinese', name: '中文', flag: '🇨🇳' }
  ];

  const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

  // Handle WhatsApp click
  const handleWhatsAppClick = (e) => {
    e.preventDefault();
    const phoneNumber = '250788350577';
    const message = encodeURIComponent(`Hello! I'm interested in Urugo Women Opportunity Center programs.`);
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  // Handle scroll and resize to show/hide top bar
  useEffect(() => {
    const handleScroll = () => {
      const isTop = window.scrollY <= 10;
      const isDesktop = window.innerWidth > 768;
      setShowTopBar(isTop && isDesktop);
      setIsScrolled(window.scrollY > 50);
    };

    const handleResize = () => {
      const isDesktop = window.innerWidth > 768;
      const isTop = window.scrollY <= 10;
      setShowTopBar(isTop && isDesktop);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Measure top bar height when it becomes visible
  useEffect(() => {
    if (showTopBar && topBarRef.current) {
      setTopBarHeight(topBarRef.current.offsetHeight);
    } else {
      setTopBarHeight(0);
    }
  }, [showTopBar]);

  const toggleLanguageDropdown = () => {
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
  };

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    setIsLanguageDropdownOpen(false);
    setIsMenuOpen(false);
  };

  const handleHashNavigation = (hash, e) => {
    if (e) e.preventDefault();
    setIsMenuOpen(false);
    setIsLanguageDropdownOpen(false);
    
    if (window.location.pathname !== '/') {
      window.location.href = `/${hash}`;
    } else {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        setTimeout(() => {
          const el = document.querySelector(hash);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  };

  const handleHomeClick = (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    setIsLanguageDropdownOpen(false);
    
    if (window.location.pathname !== '/') {
      window.location.href = '/';
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Animated Hire Button Effect (only on desktop)
  useEffect(() => {
    const hireButton = hireButtonRef.current;
    if (!hireButton || window.innerWidth <= 992) return;

    let position = 0;
    let direction = 1;
    const speed = 0.5;
    const maxDistance = 10;

    const animate = () => {
      if (!isHovered) {
        position += direction * speed;
        if (position >= maxDistance) direction = -1;
        else if (position <= -maxDistance) direction = 1;
        hireButton.style.transform = `translateX(${position}px)`;
      } else {
        hireButton.style.transform = 'translateX(0)';
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isHovered]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isLanguageDropdownOpen && !event.target.closest('.language-switcher')) {
        setIsLanguageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLanguageDropdownOpen]);

  // Handle trainee registration modal open
  const handleTraineeRegister = () => {
    setShowTraineeModal(true);
    setIsMenuOpen(false);
  };

  // Open search modal
  const openSearchModal = () => {
    setShowSearchModal(true);
    setIsMenuOpen(false);
  };

  // Close search modal
  const closeSearchModal = () => {
    setShowSearchModal(false);
  };

  // Lock body scroll when search modal is open + auto focus input
  useEffect(() => {
    if (showSearchModal) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        const input = document.querySelector('.search-modal-content .search-input');
        if (input) input.focus();
      }, 100);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showSearchModal]);

  // Close search modal on ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && showSearchModal) {
        closeSearchModal();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showSearchModal]);

  return (
    <>
      {/* Top Bar - Contact Info & Social Media */}
      {showTopBar && (
        <div className="top-bar" ref={topBarRef}>
          <div className="top-bar-container">
            <div className="top-bar-left">
              <span><FaMapMarkerAlt /> 3FHW+W33 Kayonza</span>
              <span><FaEnvelope /> info@urugowoc.com</span>
              <span><FaPhoneAlt /> +250 788 350 577</span>
            </div>
            <div className="top-bar-right">
              <a 
                href="https://web.facebook.com/search/top/?q=urugo%20women%27s%20opportunity%20center" 
                target="_blank" 
                rel="noopener noreferrer"
                title={t('facebook') || 'Facebook'}
              >
                <FaFacebook />
              </a>
              <a 
                href="https://x.com/UrugoWoc" 
                target="_blank" 
                rel="noopener noreferrer"
                title={t('twitter') || 'Twitter'}
              >
                <FaTwitter />
              </a>
              <a 
                href="https://www.instagram.com/urugo_woc?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" 
                target="_blank" 
                rel="noopener noreferrer"
                title={t('instagram') || 'Instagram'}
              >
                <FaInstagram />
              </a>
              <a 
                href="https://www.youtube.com/@urugowoc3033" 
                target="_blank" 
                rel="noopener noreferrer"
                title={t('youtube') || 'YouTube'}
              >
                <FaYoutube />
              </a>
              <a 
                href="#" 
                onClick={handleWhatsAppClick} 
                title={t('whatsapp') || 'WhatsApp'}
              >
                <FaWhatsapp />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Main Header */}
      <header 
        className={`header ${isScrolled ? 'header-scrolled' : ''}`}
        style={{ top: showTopBar ? `${topBarHeight}px` : '0' }}
      >
        <div className="header-container">
          <div className="header-content">
            <div className="header-logo">
              <Link to="/" onClick={handleHomeClick}>
                {!isScrolled ? (
                  <img src='/1768314736614-removebg-preview.png' alt="Urugo WOC Logo" className="header-logo-img" />
                ) : (
                  <span className="header-logo-text">WfWI/URUGO WOC</span>
                )}
                <span className="sr-only">WfWI/URUGO WOC</span>
              </Link>
            </div>

            {/* Search Icon Button */}
            <button
              className="header-search-icon-btn"
              onClick={openSearchModal}
              aria-label="Open search"
              title={t('search') || 'Search'}
            >
              <FaSearch />
            </button>

            <button 
              className="header-mobile-menu-btn"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMenuOpen ? <FaTimes /> : <FaBars />}
            </button>

            <nav className={`header-nav ${isMenuOpen ? 'header-nav-open' : ''}`}>
              <a href="/" className="header-nav-link" onClick={handleHomeClick}>
                <span className="header-nav-icon-wrapper">
                  <FaHome className="header-icon" />
                </span>
                <span className="header-nav-text">{t('home')}</span>
              </a>
              <Link to="/about" className="header-nav-link" onClick={() => {
                setIsMenuOpen(false);
                setIsLanguageDropdownOpen(false);
              }}>
                <span className="header-nav-icon-wrapper">
                  <FaInfoCircle className="header-icon" />
                </span>
                <span className="header-nav-text">{t('about')}</span>
              </Link>
              <Link to="/#departments" className="header-nav-link" onClick={(e) => handleHashNavigation('#departments', e)}>
                <span className="header-nav-icon-wrapper">
                  <FaBuilding className="header-icon" />
                </span>
                <span className="header-nav-text">{t('departments')}</span>
              </Link>
              <Link to="/#women" className="header-nav-link" onClick={(e) => handleHashNavigation('#women', e)}>
                <span className="header-nav-icon-wrapper">
                  <FaFemale className="header-icon" />
                </span>
                <span className="header-nav-text">{t('women')}</span>
              </Link>
              <Link to="/gallery" className="header-nav-link" onClick={() => {
                setIsMenuOpen(false);
                setIsLanguageDropdownOpen(false);
              }}>
                <span className="header-nav-icon-wrapper">
                  <FaImages className="header-icon" />
                </span>
                <span className="header-nav-text">{t('gallery')}</span>
              </Link>

              <Link to="/stats" className="header-nav-link" onClick={() => {
                setIsMenuOpen(false);
                setIsLanguageDropdownOpen(false);
              }}>
                <span className="header-nav-icon-wrapper">
                  <FaChartBar className="header-icon" />
                </span>
                <span className="header-nav-text">{t('stats') || 'Stats'}</span>
              </Link>

              <button 
                ref={hireButtonRef}
                className="header-hire-button"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={(e) => handleHashNavigation('#departments', e)}
              >
                <FaBriefcase className="header-hire-icon" />
                <span>{t('hire') || 'Hire Talent'}</span>
                <span className="header-pulse-dot"></span>
              </button>

              {/* Login / Logout - Icon only */}
              {user ? (
                <button 
                  className="header-btn header-btn-icon" 
                  onClick={onLogout}
                  title={t('logout') || 'Logout'}
                  aria-label={t('logout') || 'Logout'}
                >
                  <FaSignOutAlt className="header-icon" />
                </button>
              ) : (
                <button 
                  className="header-btn header-btn-icon" 
                  onClick={onLoginClick}
                  title={t('login') || 'Login'}
                  aria-label={t('login') || 'Login'}
                >
                  <FaSignInAlt className="header-icon" />
                </button>
              )}

              {/* Language switcher - Flag only */}
              <div className="language-switcher">
                <button 
                  className="language-btn language-btn-flag-only" 
                  onClick={toggleLanguageDropdown}
                  title={currentLanguage.name}
                  aria-label={`Language: ${currentLanguage.name}`}
                >
                  <span className="language-flag-only">{currentLanguage.flag}</span>
                  {isLanguageDropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                
                {isLanguageDropdownOpen && (
                  <div className="language-dropdown">
                    {languages.map(lang => (
                      <button
                        key={lang.code}
                        className={`language-option ${language === lang.code ? 'active' : ''}`}
                        onClick={() => handleLanguageChange(lang.code)}
                      >
                        <span className="flag">{lang.flag}</span>
                        <span className="name">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Search Modal */}
      {showSearchModal && (
        <div className="search-modal-overlay" onClick={closeSearchModal}>
          <div
            className="search-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="search-modal-header">
              <div className="search-modal-title">
                <FaSearch className="search-modal-title-icon" />
                <h2>{t('search') || 'Search'}</h2>
              </div>
              <button
                className="search-modal-close"
                onClick={closeSearchModal}
                aria-label="Close search"
              >
                <FaTimes />
              </button>
            </div>

            <div className="search-modal-body">
              <SearchBar isModal={true} autoFocus={true} />
            </div>

            <div className="search-modal-hint">
              <p>
                {t('searchHint') ||
                  'Search for women, departments, activities, and more'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Trainee Registration Modal */}
      <TraineeRegistrationModal
        isOpen={showTraineeModal}
        onClose={() => setShowTraineeModal(false)}
        user={user}
      />
    </>
  );
};

export default Header;